'use client';

import React, { useEffect, useRef } from 'react';

export default function BentoFeatures() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.querySelectorAll('.reveal-blur').forEach((el, i) => {
              setTimeout(() => el.classList.add('is-visible'), i * 120);
            });
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="features" ref={sectionRef} className="py-16 sm:py-24 px-4 sm:px-6 relative">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-14 reveal-blur">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[rgba(124,58,237,0.25)] mb-6">
            <span className="text-[10px] font-mono text-violet-light tracking-widest uppercase">Platform Features</span>
          </div>
          <h2
            className="font-manrope font-extrabold tracking-tighter text-[#EEEEF0] leading-tight max-w-3xl"
            style={{ fontSize: 'clamp(2rem, 4vw, 3.25rem)' }}
          >
            Every workflow, one surface.
          </h2>
          <p className="text-[#9999A8] text-lg mt-4 max-w-xl">
            Four core modules — each solving a distinct pain point, each visible from the same command center.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-12 gap-4 auto-rows-auto">

          {/* Cell 1: Lease Lifecycle — large, spans 7 cols × 2 rows */}
          <div className="col-span-12 lg:col-span-7 bento-card card-shine reveal-blur p-5 sm:p-8">
            <div className="grid md:grid-cols-2 gap-8 h-full">
              <div className="flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-[rgba(124,58,237,0.15)] border border-[rgba(124,58,237,0.25)] flex items-center justify-center">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </div>
                    <span className="text-xs font-mono text-violet-light tracking-widest uppercase">Lease Lifecycle</span>
                  </div>
                  <h3 className="font-manrope font-bold text-2xl text-[#EEEEF0] tracking-tight mb-3">
                    Never miss a renewal window again.
                  </h3>
                  <p className="text-[#9999A8] text-sm leading-relaxed">
                    Automated 90/60/30-day renewal alerts. Lease end dates, rent escalation clauses, and tenant contact info — visible at a glance across every door.
                  </p>
                </div>
                <div className="flex items-baseline gap-2 mt-6">
                  <span className="text-3xl font-manrope font-extrabold number-gradient-violet">98%</span>
                  <span className="text-sm text-[#9999A8] font-mono">lease renewal rate for active users</span>
                </div>
              </div>

              {/* Lease timeline mini-UI */}
              <div className="bg-[#13131A] rounded-xl border border-[rgba(255,255,255,0.04)] p-4 flex flex-col gap-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-mono text-[#9999A8]">Upcoming Expirations</span>
                  <span className="text-[10px] font-mono text-[#A78BFA]">Next 90 days</span>
                </div>
                {[
                  { unit: 'Unit 3B — Westgate', name: 'M. Kowalski', days: 12, status: 'urgent' },
                  { unit: 'Unit 7A — Harbor View', name: 'J. Okafor', days: 28, status: 'warning' },
                  { unit: 'Unit 1F — Birchwood', name: 'S. Patel', days: 45, status: 'ok' },
                  { unit: 'Unit 12C — Sycamore', name: 'R. Nguyen', days: 67, status: 'ok' },
                ]?.map((lease) => (
                  <div key={lease?.unit} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#1C1C24] transition-colors">
                    <div
                      className="w-1.5 h-1.5 rounded-full shrink-0"
                      style={{
                        background: lease?.status === 'urgent' ? '#EF4444' : lease?.status === 'warning' ? '#F59E0B' : '#22C55E',
                        boxShadow: lease?.status === 'urgent' ? '0 0 6px rgba(239,68,68,0.6)' : undefined,
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] text-[#EEEEF0] truncate font-mono">{lease?.unit}</div>
                      <div className="text-[10px] text-[#5A5A6E]">{lease?.name}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div
                        className="text-xs font-mono font-bold"
                        style={{ color: lease?.status === 'urgent' ? '#EF4444' : lease?.status === 'warning' ? '#F59E0B' : '#22C55E' }}
                      >
                        {lease?.days}d
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Cell 2: Rent Reminders — 5 cols */}
          <div className="col-span-12 lg:col-span-5 bento-card card-shine reveal-blur p-5 sm:p-8 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-[rgba(34,197,94,0.1)] border border-[rgba(34,197,94,0.2)] flex items-center justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                </div>
                <span className="text-xs font-mono text-[#22C55E] tracking-widest uppercase">Rent Automation</span>
              </div>
              <h3 className="font-manrope font-bold text-xl text-[#EEEEF0] tracking-tight mb-3">
                Automated reminders that get paid.
              </h3>
              <p className="text-[#9999A8] text-sm leading-relaxed mb-6">
                SMS and email sequences fire automatically at day 1, 3, and 7 overdue. No more manual follow-ups. Tenants pay faster because the system never forgets.
              </p>
            </div>

            {/* Reminder flow */}
            <div className="flex flex-col gap-2">
              {[
                { day: 'Day 0', action: 'Rent due — auto receipt sent', color: '#A78BFA', done: true },
                { day: 'Day 1', action: 'Friendly SMS reminder fired', color: '#A78BFA', done: true },
                { day: 'Day 3', action: 'Email + portal nudge sent', color: '#F59E0B', done: true },
                { day: 'Day 7', action: 'Late fee applied automatically', color: '#EF4444', done: false },
              ]?.map((step, i) => (
                <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-[#13131A] border border-[rgba(255,255,255,0.03)]">
                  <div
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ background: step?.done ? step?.color : '#3A3A4A', boxShadow: step?.done ? `0 0 6px ${step?.color}80` : undefined }}
                  />
                  <span className="text-[10px] font-mono text-[#5A5A6E] w-12 shrink-0">{step?.day}</span>
                  <span className="text-[11px] text-[#9999A8] flex-1">{step?.action}</span>
                  {step?.done && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
              ))}
            </div>

            <div className="flex items-baseline gap-2 mt-6">
              <span className="text-3xl font-manrope font-extrabold" style={{ color: '#22C55E' }}>−47%</span>
              <span className="text-sm text-[#9999A8] font-mono">late payments vs. manual follow-up</span>
            </div>
          </div>

          {/* Cell 3: Maintenance Workflow — 5 cols */}
          <div className="col-span-12 lg:col-span-5 bento-card card-shine reveal-blur p-5 sm:p-8 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-[rgba(245,158,11,0.1)] border border-[rgba(245,158,11,0.2)] flex items-center justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                  </svg>
                </div>
                <span className="text-xs font-mono text-[#F59E0B] tracking-widest uppercase">Maintenance</span>
              </div>
              <h3 className="font-manrope font-bold text-xl text-[#EEEEF0] tracking-tight mb-3">
                Tickets routed, resolved, documented.
              </h3>
              <p className="text-[#9999A8] text-sm leading-relaxed mb-6">
                Tenants submit via SMS or portal. Tickets auto-route to your preferred vendors with SLA timers. Every resolution is timestamped for lease-end disputes.
              </p>
            </div>

            {/* Ticket kanban mini */}
            <div className="grid grid-cols-3 gap-2">
              {[
                { status: 'New', count: 3, color: '#A78BFA', items: ['HVAC Unit 3B', 'Window 7A', 'Lock 12C'] },
                { status: 'In Progress', count: 2, color: '#F59E0B', items: ['Plumbing 4D', 'Paint 9B'] },
                { status: 'Resolved', count: 8, color: '#22C55E', items: ['Roof patch', 'Appliance', 'Electrical'] },
              ]?.map((col) => (
                <div key={col?.status} className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ background: col?.color }} />
                    <span className="text-[9px] font-mono text-[#5A5A6E] uppercase tracking-wider">{col?.status}</span>
                    <span className="text-[9px] font-mono ml-auto" style={{ color: col?.color }}>{col?.count}</span>
                  </div>
                  {col?.items?.map((item) => (
                    <div key={item} className="p-1.5 rounded bg-[#13131A] border border-[rgba(255,255,255,0.03)] text-[10px] text-[#9999A8] font-mono truncate">
                      {item}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div className="flex items-baseline gap-2 mt-6">
              <span className="text-3xl font-manrope font-extrabold" style={{ color: '#F59E0B' }}>1.4d</span>
              <span className="text-sm text-[#9999A8] font-mono">avg resolution time across portfolio</span>
            </div>
          </div>

          {/* Cell 4: Investor Reporting — 7 cols */}
          <div className="col-span-12 lg:col-span-7 bento-card card-shine reveal-blur p-5 sm:p-8">
            <div className="grid md:grid-cols-2 gap-8 h-full">
              <div className="flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-[rgba(124,58,237,0.15)] border border-[rgba(124,58,237,0.25)] flex items-center justify-center">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
                      </svg>
                    </div>
                    <span className="text-xs font-mono text-violet-light tracking-widest uppercase">Investor Reporting</span>
                  </div>
                  <h3 className="font-manrope font-bold text-xl text-[#EEEEF0] tracking-tight mb-3">
                    CFO-grade reports. No CFO required.
                  </h3>
                  <p className="text-[#9999A8] text-sm leading-relaxed">
                    One-click PDF reports with NOI, cap rate, occupancy trend, and cash flow per property. Syndicators send investor updates in 4 minutes instead of 4 hours.
                  </p>
                </div>
                <div className="flex items-baseline gap-3 mt-6">
                  <span className="text-3xl font-manrope font-extrabold number-gradient-violet">4 min</span>
                  <span className="text-sm text-[#9999A8] font-mono">to generate full investor report</span>
                </div>
              </div>

              {/* Mini report card */}
              <div className="bg-[#13131A] rounded-xl border border-[rgba(255,255,255,0.04)] p-4 flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono text-[#9999A8]">Q1 2026 Summary</span>
                  <span className="px-2 py-0.5 rounded bg-[rgba(124,58,237,0.15)] text-[10px] font-mono text-violet-light">PDF ready</span>
                </div>

                {/* Chart bars */}
                <div className="flex items-end gap-2 h-20">
                  {[
                    { month: 'Oct', val: 72, color: '#4C1D95' },
                    { month: 'Nov', val: 80, color: '#5B21B6' },
                    { month: 'Dec', val: 78, color: '#6D28D9' },
                    { month: 'Jan', val: 88, color: '#7C3AED' },
                    { month: 'Feb', val: 94, color: '#A78BFA' },
                    { month: 'Mar', val: 91, color: '#A78BFA' },
                  ]?.map((bar) => (
                    <div key={bar?.month} className="flex-1 flex flex-col items-center gap-1">
                      <div
                        className="w-full rounded-t-sm transition-all duration-700"
                        style={{
                          height: `${bar?.val}%`,
                          background: bar?.color,
                          boxShadow: bar?.month === 'Feb' ? `0 0 10px ${bar?.color}80` : undefined,
                        }}
                      />
                      <span className="text-[8px] font-mono text-[#5A5A6E]">{bar?.month}</span>
                    </div>
                  ))}
                </div>

                {/* Metrics row */}
                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[rgba(255,255,255,0.04)]">
                  {[
                    { label: 'NOI', val: 'R48,200', color: '#22C55E' },
                    { label: 'Cap Rate', val: '6.2%', color: '#A78BFA' },
                    { label: 'Occupancy', val: '94%', color: '#22C55E' },
                    { label: 'Cash-on-Cash', val: '8.1%', color: '#A78BFA' },
                  ]?.map((m) => (
                    <div key={m?.label} className="flex flex-col gap-0.5">
                      <span className="text-[9px] font-mono text-[#5A5A6E] uppercase">{m?.label}</span>
                      <span className="text-sm font-mono font-bold" style={{ color: m?.color }}>{m?.val}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}